import { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
  Tooltip,
  ReferenceDot
} from "recharts";

import { CustomTooltip } from "./tooltip";
import { legendItems } from "./const";
import { useBreakLG } from "@monta/design-system";

import type { FC } from "react";
import type { Props, ChartDataEntry, BarClickData } from "./types";

const HorizontalStackedBarChart: FC<Props> = ({
  data,
  keys,
  colors,
  title,
  colorAccessor
}) => {
  const [hoveredBar, setHoveredBar] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const isDesktop = useBreakLG();

  const getRowTotal = (datum: ChartDataEntry) =>
    keys.reduce((sum, k) => sum + Number(datum?.[k] ?? 0), 0);

  const getBarFill = (keyIndex: number, dataIndex: number) => {
    const defaultColor = colors[keyIndex] || "#8884d8";
    if (typeof colorAccessor === "function") {
      const datum: ChartDataEntry = data[dataIndex];
      const computed = colorAccessor({
        key: keys[keyIndex],
        keyIndex,
        datum,
        dataIndex,
        defaultColor
      });
      if (computed) return computed;
    }

    if (activeIndex === dataIndex) {
      return defaultColor;
    }

    if (hoveredBar === keys[keyIndex]) {
      return defaultColor;
    }

    return defaultColor;
  };

  const getBarFilter = (keyIndex: number, dataIndex: number) => {
    if (activeIndex === dataIndex) {
      return "brightness(0.9) saturate(1.3) drop-shadow(0 4px 8px rgba(0,0,0,0.15))";
    }

    if (hoveredBar === keys[keyIndex]) {
      return "brightness(1.05) saturate(1.1)";
    }

    return "none";
  };

  const ticks = isDesktop
    ? [-33, 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
    : [-33, 0, 25, 50, 75, 100];

  const tooltipContent = (props) => <CustomTooltip {...props} data={data} />;

  // Compute a responsive minHeight based on data length
  // Ensures enough vertical space so bars and labels do not overlap
  const estimatedRowHeight = isDesktop ? 48 : 36; // per category row
  const containerPadding = isDesktop ? 64 : 48; // axes/legends spacing
  const responsiveMinHeight = Math.max(
    240,
    (Array.isArray(data) ? data.length : 0) * estimatedRowHeight +
      containerPadding
  );

  return (
    <div
      className="w-full flex lg:flex-row sm:flex-col  lg:gap-5 justify-between"
      aria-labelledby={title ?? "chart-title"}
    >
      <div className="flex flex-col lg:w-max sm:w-full order-2 lg:order-1 mt-4 ">
        <div className="flex items-center mb-4">
          <b className="text-sm font-semibold ">راهنمای نمودار</b>
        </div>

        <div className="flex flex-wrap lg:flex-nowrap lg:flex-col gap-y-4">
          {legendItems.map((item, index) => (
            <div
              key={`${item.label}-${index}`}
              className="flex items-center w-1/2 relative"
            >
              <span
                className={`text-[#475467] text-xs font-medium before:absolute before:top-[1px] before:content-[''] before:w-3 before:h-3 before:rounded-full before:inline-block before:ml-2 before:mr-0 ${item.colorClass}`}
              >
                <span className="mr-6 whitespace-nowrap print:pr-3">
                  {item.label}
                </span>
              </span>
            </div>
          ))}
        </div>

        <p className="mt-5 pb-6 text-[#667085] text-[10px] font-medium">
          نوار کم رنگ هر ستون، نمایانگر نمره بدون احتساب نمره منفی است.
        </p>
      </div>

      <div className="w-full lg:order-2 relative">
        <ResponsiveContainer
          width="100%"
          height="100%"
          minHeight={responsiveMinHeight}
          className="order-1 lg:order-2"
        >
          <BarChart
            data={data}
            layout="vertical"
            barCategoryGap={1}
            // barGap={5}
            barSize={isDesktop ? 20 : 10}
            maxBarSize={isDesktop ? 20 : 10}
            aria-describedby={title ? `chart-description-${title}` : undefined}
            className="transition-all duration-300 ease-in-out"
          >
            <CartesianGrid opacity={0.4} />

            <XAxis
              type="number"
              domain={[0, 100]}
              tick={{
                fontSize: isDesktop ? 12 : 10,
                fontWeight: 400,
                color: "#98A2B3",
                direction: "ltr"
              }}
              axisLine={{ stroke: "#E1E7EC" }}
              ticks={ticks}
              angle={isDesktop ? 0 : 60}
              tickMargin={16}
              interval={0}
              tickLine={false}
              // minTickGap={10}

              allowDataOverflow={false}
            />
            <YAxis
              dataKey="name"
              type="category"
              tick={{
                color: "#344054",
                fontSize: isDesktop ? 12 : 10,
                fontWeight: isDesktop ? 500 : 400,
                direction: "rtl",
                textAnchor: "middle"
              }}
              axisLine={{ stroke: "#E1E7EC" }}
              width={isDesktop ? 100 : 70}
              tickMargin={isDesktop ? 60 : 20}
              tickLine={false}
              tickSize={10}
            />
            {/* Zero-value indicator: show a small dot at x=0 when the stacked total is 0 */}
            {Array.isArray(data)
              ? data.map((d: ChartDataEntry, i: number) =>
                  getRowTotal(d) === 0 ? (
                    <ReferenceDot
                      key={`zero-ref-${d.name}-${i}`}
                      x={0}
                      y={d.name}
                      r={isDesktop ? 3 : 2}
                      fill="#98A2B3"
                      stroke="#98A2B3"
                    />
                  ) : null
                )
              : null}
            <Tooltip
              content={tooltipContent}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
              animationDuration={200}
              wrapperStyle={{ outline: "none" }}
            />
            {keys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                stackId="stack"
                fill={colors[index] || "#8884d8"}
                radius={index === 0 ? [0, 4, 4, 0] : [0, 8, 8, 0]}
                onMouseEnter={() => setHoveredBar(key)}
                onMouseLeave={() => setHoveredBar(null)}
                onClick={(data: BarClickData) => {
                  setActiveIndex(data.index);
                  // Add haptic feedback for mobile
                  if (navigator.vibrate) {
                    navigator.vibrate(50);
                  }
                }}
                style={{
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  filter:
                    hoveredBar === key
                      ? "brightness(1.1) saturate(1.2)"
                      : "none",
                  transform: hoveredBar === key ? "scale(1.02)" : "scale(1)"
                }}
              >
                {data.map((_: ChartDataEntry, dataIndex: number) => (
                  <Cell
                    key={`cell-${key}-${dataIndex}`}
                    fill={getBarFill(index, dataIndex)}
                    style={{
                      filter: getBarFilter(index, dataIndex),
                      transition: "all 0.2s ease-in-out"
                    }}
                  />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default HorizontalStackedBarChart;
